/**
 * Pure geometry for the bulk align/distribute diagram toolbar actions.
 *
 * Each diagram maps its selected React Flow nodes to `AlignableNode` /
 * `SectionedAlignableNode` (resolving sizes via `getNodeMeasuredWidthHeight`),
 * then applies the returned position updates through its own state path: the
 * estate waterfall dispatches reducer actions, and the other diagrams go
 * through the shared `useAlignNodes` hook.
 */

import { groupBy, uniq } from 'lodash';

export type AlignKind =
  // Align left edges to the leftmost selected node
  | 'left'
  // Align right edges to the rightmost selected node
  | 'right'
  // Align top edges to the highest selected node
  | 'top'
  // Align bottom edges to the lowest selected node
  | 'bottom'
  // Product "center": align x-centers to the bounding box's vertical mid-axis
  | 'centerX'
  // Product "middle": align y-centers to the bounding box's horizontal mid-axis
  | 'centerY';

/** Axis along which selected nodes have their spacing equalized. */
export type DistributeAxis = 'horizontal' | 'vertical';

export interface AlignableNode {
  id: string;
  /** Top-left corner, in flow coordinates (React Flow convention). */
  position: { x: number; y: number };
  width: number;
  height: number;
}

export interface SectionedAlignableNode extends AlignableNode {
  /**
   * Vertical band the node is confined to (e.g. the waterfall's death
   * sections). Undefined for free-floating nodes (text notes) and diagrams
   * without sections.
   */
  section?: string;
}

// Y-axis alignment and distribution are constrained to a node's section so
// nodes never cross vertical bands; x-axis alignment spans the whole
// selection.
export const SECTION_SCOPED_ALIGN_KINDS: AlignKind[] = [
  'top',
  'bottom',
  'centerY',
];

export interface NodePositionUpdate {
  id: string;
  position: { x: number; y: number };
}

interface BoundingBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// Positions within this distance are treated as already aligned, so no-op
// updates don't generate history entries or dirty state.
const POSITION_EPSILON = 1e-6;

// Minimum edge-to-edge gap used when a selection is too cramped to distribute
// within its current extent. If the evenly-computed gap falls below this, the
// tiles would overlap or touch, so we fall back to this fixed gap and let the
// far tile shift outward instead. Matches the auto-layout node separation
// (dagre `nodesep`) so hand-distributed tiles look native.
const MIN_DISTRIBUTE_GAP = 50;

// Nodes whose leading edges sit within this distance are treated as one
// row/column when ordering a distribution, so nearly aligned tiles order in
// reading direction (left→right within a row, top→bottom within a column)
// instead of by a few stray pixels of drag offset.
const DISTRIBUTE_ROW_TOLERANCE = 10;

function getBoundingBox(nodes: AlignableNode[]): BoundingBox {
  return {
    left: Math.min(...nodes.map((n) => n.position.x)),
    right: Math.max(...nodes.map((n) => n.position.x + n.width)),
    top: Math.min(...nodes.map((n) => n.position.y)),
    bottom: Math.max(...nodes.map((n) => n.position.y + n.height)),
  };
}

function alignedPosition(
  node: AlignableNode,
  kind: AlignKind,
  bbox: BoundingBox
): { x: number; y: number } {
  const { x, y } = node.position;
  switch (kind) {
    case 'left':
      return { x: bbox.left, y };
    case 'right':
      return { x: bbox.right - node.width, y };
    case 'top':
      return { x, y: bbox.top };
    case 'bottom':
      return { x, y: bbox.bottom - node.height };
    case 'centerX':
      return { x: (bbox.left + bbox.right) / 2 - node.width / 2, y };
    case 'centerY':
      return { x, y: (bbox.top + bbox.bottom) / 2 - node.height / 2 };
  }
}

/**
 * Computes new positions aligning `nodes` along the given edge or axis of
 * their collective bounding box. Returns updates only for nodes that actually
 * move; empty for fewer than two nodes.
 */
export function computeAlignmentChanges(
  nodes: AlignableNode[],
  kind: AlignKind
): NodePositionUpdate[] {
  if (nodes.length < 2) return [];

  const bbox = getBoundingBox(nodes);

  const updates: NodePositionUpdate[] = [];
  for (const node of nodes) {
    const position = alignedPosition(node, kind, bbox);
    const moved =
      Math.abs(position.x - node.position.x) > POSITION_EPSILON ||
      Math.abs(position.y - node.position.y) > POSITION_EPSILON;
    if (moved) updates.push({ id: node.id, position });
  }
  return updates;
}

/**
 * Orders nodes along the distribution axis. Nodes whose leading edges sit
 * within DISTRIBUTE_ROW_TOLERANCE of a cluster's first node count as one
 * row/column and order along the cross axis (then id for determinism), so
 * coincident or nearly aligned tiles fan out in reading direction — a
 * left-to-right stairstep for vertical distribution, top-to-bottom for
 * horizontal. Clustering is an explicit pass because a tolerance-based
 * pairwise comparator wouldn't be transitive.
 */
function sortForDistribution(
  nodes: AlignableNode[],
  axis: DistributeAxis
): AlignableNode[] {
  const primary = (node: AlignableNode) =>
    axis === 'vertical' ? node.position.y : node.position.x;
  const cross = (node: AlignableNode) =>
    axis === 'vertical' ? node.position.x : node.position.y;

  const byPrimary = [...nodes].sort(
    (a, b) =>
      primary(a) - primary(b) || cross(a) - cross(b) || a.id.localeCompare(b.id)
  );

  const clusters: AlignableNode[][] = [];
  for (const node of byPrimary) {
    const cluster = clusters[clusters.length - 1];
    // cluster[0] is safe: clusters are only ever created with one member.
    if (
      cluster &&
      primary(node) - primary(cluster[0]!) <= DISTRIBUTE_ROW_TOLERANCE
    ) {
      cluster.push(node);
    } else {
      clusters.push([node]);
    }
  }

  return clusters.flatMap((cluster) =>
    [...cluster].sort((a, b) => cross(a) - cross(b) || a.id.localeCompare(b.id))
  );
}

/**
 * Computes new x positions equalizing the horizontal gaps between adjacent
 * nodes (PowerPoint "distribute horizontally" semantics): the leftmost and
 * rightmost nodes stay fixed and the nodes between them are spaced evenly.
 * When the selection is too cramped for a positive gap, the tiles are spaced
 * at `MIN_DISTRIBUTE_GAP` starting from the leftmost tile — the rightmost tile
 * shifts out so tiles never overlap or touch. Returns updates only for nodes
 * that actually move; empty for fewer than three nodes.
 */
export function computeHorizontalDistributionChanges(
  nodes: AlignableNode[]
): NodePositionUpdate[] {
  if (nodes.length < 3) return [];

  const sorted = sortForDistribution(nodes, 'horizontal');

  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  const middle = sorted.slice(1, -1);

  const innerSpace = last.position.x - (first.position.x + first.width);
  const middleWidths = middle.reduce((sum, n) => sum + n.width, 0);
  const evenGap = (innerSpace - middleWidths) / (sorted.length - 1);

  // Roomy: keep both end tiles fixed and space the middle evenly. Cramped:
  // fall back to a fixed minimum gap and re-space every tile after the first
  // (the far tile shifts out) so nothing overlaps.
  const cramped = evenGap < MIN_DISTRIBUTE_GAP;
  const gap = cramped ? MIN_DISTRIBUTE_GAP : evenGap;
  const nodesToPlace = cramped ? sorted.slice(1) : middle;

  const updates: NodePositionUpdate[] = [];
  let cursor = first.position.x + first.width + gap;
  for (const node of nodesToPlace) {
    if (Math.abs(cursor - node.position.x) > POSITION_EPSILON) {
      updates.push({
        id: node.id,
        position: { x: cursor, y: node.position.y },
      });
    }
    cursor += node.width + gap;
  }
  return updates;
}

/**
 * Vertical counterpart to `computeHorizontalDistributionChanges`: equalizes the
 * vertical gaps between adjacent nodes (PowerPoint "distribute vertically"
 * semantics). The topmost and bottommost nodes stay fixed and the nodes between
 * them are spaced evenly. When the selection is too cramped for a positive gap,
 * the tiles are spaced at `MIN_DISTRIBUTE_GAP` from the topmost tile — the
 * bottommost tile shifts down so tiles never overlap or touch. Returns updates
 * only for nodes that actually move; empty for fewer than three nodes.
 */
export function computeVerticalDistributionChanges(
  nodes: AlignableNode[]
): NodePositionUpdate[] {
  if (nodes.length < 3) return [];

  const sorted = sortForDistribution(nodes, 'vertical');

  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  const middle = sorted.slice(1, -1);

  const innerSpace = last.position.y - (first.position.y + first.height);
  const middleHeights = middle.reduce((sum, n) => sum + n.height, 0);
  const evenGap = (innerSpace - middleHeights) / (sorted.length - 1);

  // Roomy: keep both end tiles fixed and space the middle evenly. Cramped:
  // fall back to a fixed minimum gap and re-space every tile after the first
  // (the far tile shifts down) so nothing overlaps.
  const cramped = evenGap < MIN_DISTRIBUTE_GAP;
  const gap = cramped ? MIN_DISTRIBUTE_GAP : evenGap;
  const nodesToPlace = cramped ? sorted.slice(1) : middle;

  const updates: NodePositionUpdate[] = [];
  let cursor = first.position.y + first.height + gap;
  for (const node of nodesToPlace) {
    if (Math.abs(cursor - node.position.y) > POSITION_EPSILON) {
      updates.push({
        id: node.id,
        position: { x: node.position.x, y: cursor },
      });
    }
    cursor += node.height + gap;
  }
  return updates;
}

/** Dispatches to the horizontal or vertical distribution by axis. */
export function computeDistributionChanges(
  nodes: AlignableNode[],
  axis: DistributeAxis
): NodePositionUpdate[] {
  return axis === 'vertical'
    ? computeVerticalDistributionChanges(nodes)
    : computeHorizontalDistributionChanges(nodes);
}

/**
 * Section-aware alignment: y-axis kinds align each section's nodes
 * independently so nodes never cross vertical bands, while x-axis kinds align
 * across the whole selection. Free-floating nodes (no section) align against
 * the whole selection's bounding box rather than any single section, so a
 * diagram without sections degenerates to plain global alignment.
 */
export function computeSectionAwareAlignmentChanges(
  nodes: SectionedAlignableNode[],
  kind: AlignKind
): NodePositionUpdate[] {
  if (!SECTION_SCOPED_ALIGN_KINDS.includes(kind)) {
    return computeAlignmentChanges(nodes, kind);
  }

  const sectionedNodes = nodes.filter((node) => node.section !== undefined);
  const freeNodes = nodes.filter((node) => node.section === undefined);

  const bySection = groupBy(sectionedNodes, (node) => node.section);
  const updates = Object.values(bySection).flatMap((sectionNodes) =>
    computeAlignmentChanges(sectionNodes, kind)
  );

  if (freeNodes.length > 0) {
    const freeNodeIds = new Set(freeNodes.map((node) => node.id));
    updates.push(
      ...computeAlignmentChanges(nodes, kind).filter((update) =>
        freeNodeIds.has(update.id)
      )
    );
  }

  return updates;
}

/**
 * Section-aware distribution: equalizing gaps across different vertical bands
 * shuffles nodes with no visual meaning, so distribution requires the whole
 * selection to share one section (or be entirely free-floating) and no-ops
 * otherwise.
 */
export function computeSectionAwareDistributionChanges(
  nodes: SectionedAlignableNode[],
  axis: DistributeAxis
): NodePositionUpdate[] {
  const sections = uniq(nodes.map((node) => node.section));
  if (sections.length > 1) return [];
  return computeDistributionChanges(nodes, axis);
}
